import type { ActiveAlarm } from '@/types/alarm.types';

export type AlarmSeverity = 'critical' | 'warning' | 'info';

export type AlarmRowStatus = 'needs_attention' | 'acknowledged' | 'history';

/** Human-readable chip labels for each bucket (cross-surface consistency with local UI). */
export const STATUS_LABELS: Record<AlarmRowStatus, string> = {
  needs_attention: 'Needs Attention',
  acknowledged: 'Acknowledged',
  history: 'History',
};

/**
 * Canonical 3-bucket classifier. Single source of truth for the cloud alarm
 * lifecycle buckets (mirrors the SQL predicate map in alarm-query.service.ts).
 *   cleared_at SET            -> history (regardless of ack)
 *   !cleared && acked         -> acknowledged
 *   !cleared && !acked        -> needs_attention
 * underlying_cleared_at and stale_since are intentionally NOT consulted here.
 */
export function alarmBucket(a: ActiveAlarm): AlarmRowStatus {
  if (a.cleared_at) return 'history';
  if (a.acked_at) return 'acknowledged';
  return 'needs_attention';
}

export type AlarmEquipmentChip = 'BMS' | 'PCS' | 'generator' | 'meter' | 'pv' | 'ev';

export interface AlarmDisplayRow {
  id: string;
  severity: AlarmSeverity;
  status: AlarmRowStatus;
  description: string;
  equipment: string;
  equipmentType: AlarmEquipmentChip | null;
  occurredAt: Date;
  blockId?: string;
  blockName?: string;
  deviceId?: string;
  ackedBy?: string;
  ackedAt?: Date;
  /** Communication-loss (UNKNOWN) annotation — true when stale_since set and not cleared. */
  stale?: boolean;
}

export type SeverityPillVariant = 'pill' | 'dot' | 'icon';
export type AlarmRowVariant = 'compact' | 'default' | 'dense';
export type AlarmRollupBannerScope = 'site' | 'block';
export type AlarmFilterClusterVariant = 'expanded' | 'compact';

export interface SeverityCounts {
  critical: number;
  warning: number;
  info: number;
  acked: number;
  stale: number;
}

export type AlarmTimeRangeKey = '1h' | '4h' | '24h' | '7d' | '30d';

export interface AlarmFilterValue {
  severity: AlarmSeverity[];
  status: AlarmRowStatus[];
  equipment: AlarmEquipmentChip[];
  timeRange: AlarmTimeRangeKey;
  search: string;
}

/**
 * Default filter for the cloud alarm surfaces. Status defaults to
 * `needs_attention` ONLY (issue #433): an acknowledged-but-still-present alarm
 * contributes 0 to the headline view and lives only in the opt-in Acknowledged
 * bucket. Acknowledged + History are opt-in clicks. This supersedes the #290
 * default (which pre-selected both active buckets so the banner stayed loud for
 * an all-acked site) — the 2026-06-05 POD review confirmed operators read a
 * non-zero headline over acked rows as a false "needs attention" signal.
 */
export const DEFAULT_FILTER_VALUE: AlarmFilterValue = {
  severity: [],
  status: ['needs_attention'],
  equipment: [],
  timeRange: '24h',
  search: '',
};

export function statusFromActiveAlarm(a: ActiveAlarm): AlarmRowStatus {
  return alarmBucket(a);
}

/**
 * Map an alarm row's `device_type` to its equipment-filter chip.
 *
 * The cloud data layer uses the CANONICAL asset-type names — `battery_storage`
 * and `solar_pv` — not the wire-format names (`battery` / `solar`) genieos
 * sends. `ActiveAlarm.device_type` rows on the alarm-list endpoint therefore
 * carry the canonical names (see backend `event-hub-consumer.service.ts` and
 * the `config.site_devices` device_type note in CLAUDE.md). The legacy wire
 * names plus the `pcs`/`bms`/`generator`/`meter` device-class names are also
 * accepted so deep-links and older rows keep filtering correctly.
 *
 * Canonical value set handled here:
 *   bms | battery_storage              -> 'BMS'
 *   pcs                                -> 'PCS'
 *   generator                          -> 'generator'
 *   meter                              -> 'meter'
 *   pv | solar | solar_pv              -> 'pv'
 *   ev | ev_charger                    -> 'ev'
 * Anything else (incl. empty/unknown)  -> null (unfilterable by equipment).
 */
export function mapEquipmentChip(deviceType: string): AlarmEquipmentChip | null {
  const t = deviceType.toLowerCase();
  if (t === 'bms' || t === 'battery_storage') return 'BMS';
  if (t === 'pcs') return 'PCS';
  if (t === 'generator') return 'generator';
  if (t === 'meter') return 'meter';
  if (t === 'pv' || t === 'solar' || t === 'solar_pv') return 'pv';
  if (t === 'ev' || t === 'ev_charger') return 'ev';
  return null;
}

/**
 * Severity rollup for AlarmRollupBanner. Counts run over the POST-filter row
 * set, so they follow the active STATUS filter (issue #433): under the default
 * Needs-Attention view an acknowledged-but-present alarm contributes 0 to the
 * headline and is counted only when the operator opts into that bucket. This
 * supersedes the #290 "always count acked so the banner stays loud" rule — the
 * 2026-06-05 POD review treats a non-zero headline over acked rows as a false
 * signal.
 *
 * Shared by the portfolio hub (CustomerAlarms) and the site Alarms tab so the
 * two surfaces cannot drift.
 */
export function deriveSeverityCounts(rows: ActiveAlarm[]): SeverityCounts {
  let critical = 0;
  let warning = 0;
  let info = 0;
  let acked = 0;
  let stale = 0;
  for (const r of rows) {
    if (r.severity === 'critical') critical += 1;
    else if (r.severity === 'warning') warning += 1;
    else if (r.severity === 'info') info += 1;
    if (alarmBucket(r) === 'acknowledged') acked += 1;
    if (r.stale_since && !r.cleared_at) stale += 1;
  }
  return { critical, warning, info, acked, stale };
}

export function toDisplayRow(a: ActiveAlarm): AlarmDisplayRow {
  return {
    id: `${a.device_id}:${a.condition_key}`,
    severity: a.severity,
    status: statusFromActiveAlarm(a),
    description: a.condition_label,
    equipment: a.device_id,
    equipmentType: mapEquipmentChip(a.device_type),
    occurredAt: new Date(a.raised_at),
    blockId: a.block_id ?? undefined,
    deviceId: a.device_id,
    ackedBy: a.acked_by ?? undefined,
    ackedAt: a.acked_at ? new Date(a.acked_at) : undefined,
    stale: !!a.stale_since && !a.cleared_at,
  };
}
